/**
 * Protocol Sorting Utility
 * Provides consistent sorting order across web app views and exports.
 */

import { getEffectivePrimaryCategory } from './healthCategories';

export const SORT_OPTIONS = [
  { value: 'default', label: 'Default (Category & Name)' },
  { value: 'healthCategory', label: 'Health Category (A–Z)' },
  { value: 'alphabetical', label: 'Alphabetical (A–Z)' },
  { value: 'startDate', label: 'Start Date (Newest First)' },
  { value: 'endDate', label: 'End Date (Newest First)' }
];

export const ALL_CATEGORIES = ['Supplement', 'Peptide', 'Hormones & AAS', 'Hormone'];

/**
 * Canonical compound-type categories.
 */
export const CANONICAL_CATEGORIES = ['Supplement', 'Peptide', 'Hormones & AAS'];

/**
 * Legacy / display aliases mapped to their canonical category.
 */
export const CATEGORY_ALIASES = {
  'Hormone': 'Hormones & AAS',
  'Hormones': 'Hormones & AAS',
  'Hormones & AAS': 'Hormones & AAS',
  'Supplements': 'Supplement',
  'Supplement': 'Supplement',
  'Peptides': 'Peptide',
  'Peptide': 'Peptide',
};

/**
 * Normalize a compound category to its canonical value.
 * Unknown / empty values are returned unchanged.
 */
export function normalizeCompoundCategory(c) {
  if (c === null || c === undefined) return c;
  const key = String(c).trim();
  if (!key) return c;
  if (Object.prototype.hasOwnProperty.call(CATEGORY_ALIASES, key)) {
    return CATEGORY_ALIASES[key];
  }
  return c;
}

/**
 * Format a protocol's dosage range consistently.
 * Returns `min unit – max unit` (en-dash) when a max range exists,
 * otherwise `min unit`. Falls back to 'N/A' when no dosage is set.
 */
export function formatDosageRange(protocol = {}) {
  const { dosageMin, dosageMax, unit } = protocol;
  const u = unit || '';
  const hasMin = dosageMin !== null && dosageMin !== undefined && dosageMin !== '';
  if (!hasMin) return 'N/A';
  const maxNum = dosageMax !== null && dosageMax !== undefined && dosageMax !== '' ? Number(dosageMax) : null;
  const minNum = Number(dosageMin);
  if (maxNum !== null && !Number.isNaN(maxNum) && !Number.isNaN(minNum) && maxNum > minNum) {
    return u ? `${dosageMin} ${u} – ${dosageMax} ${u}` : `${dosageMin} – ${dosageMax}`;
  }
  return u ? `${dosageMin} ${u}` : `${dosageMin}`;
}

/**
 * Shared protocol filter used by CSV / table / image exports and views.
 * Accepts both export-style options ({ status, category, categories, sortOption })
 * and calendar-style options ({ statusFilter, categoryFilter, sortOption }).
 *
 * - `status` / `statusFilter`: 'All' (or falsy) matches every status.
 * - `categories` (array) or `category` / `categoryFilter` (single label):
 *   'All', 'All Compounds' (or falsy) match every category. Legacy values
 *   ('Hormone', 'Supplements', ...) are normalized before comparison.
 */
export function filterProtocolsByStatusCategory(protocols = [], options = {}) {
  const {
    status,
    statusFilter,
    category,
    categoryFilter,
    categories,
    sortOption = 'default',
  } = options;

  const effectiveStatus = statusFilter ?? status;
  const effectiveCategory = categoryFilter ?? category;

  let categorySet = null;
  if (Array.isArray(categories) && categories.length > 0) {
    categorySet = new Set(categories.map((c) => normalizeCompoundCategory(c)));
  } else if (effectiveCategory && effectiveCategory !== 'All Compounds' && effectiveCategory !== 'All') {
    categorySet = new Set([normalizeCompoundCategory(effectiveCategory)]);
  }

  const filtered = (Array.isArray(protocols) ? protocols : []).filter((protocol) => {
    const matchesStatus =
      !effectiveStatus || effectiveStatus === 'All' ? true : protocol.cycleStatus === effectiveStatus;

    const matchesCategory =
      !categorySet || categorySet.has(normalizeCompoundCategory(protocol.category));

    return matchesStatus && matchesCategory;
  });

  return sortProtocols(filtered, sortOption);
}

/**
 * Sort protocol array based on sortOption
 * @param {Array} protocols 
 * @param {string} sortOption - 'default' | 'alphabetical' | 'startDate' | 'endDate'
 * @returns {Array} sorted protocols array
 */
export function sortProtocols(protocols = [], sortOption = 'default') {
  if (!Array.isArray(protocols) || protocols.length <= 1) {
    return protocols;
  }

  return [...protocols].sort((a, b) => {
    switch (sortOption) {
      case 'alphabetical':
        return (a.name || '').localeCompare(b.name || '');

      case 'healthCategory': {
        const catA = getEffectivePrimaryCategory(a);
        const catB = getEffectivePrimaryCategory(b);
        const catDiff = catA.localeCompare(catB);
        if (catDiff !== 0) return catDiff;
        return (a.name || '').localeCompare(b.name || '');
      }

      case 'startDate':
        return (b.startDate || '').localeCompare(a.startDate || '');

      case 'endDate':
        if (!a.endDate && !b.endDate) return 0;
        if (!a.endDate) return 1;
        if (!b.endDate) return -1;
        return (b.endDate || '').localeCompare(a.endDate || '');

      case 'default':
      default: {
        const categoryRank = (p) => {
          const cat = normalizeCompoundCategory(p.category);
          return cat === 'Peptide'
            ? 0
            : cat === 'Hormones & AAS'
            ? 1
            : cat === 'Supplement'
            ? 2
            : 3;
        };
        const rankDiff = categoryRank(a) - categoryRank(b);
        if (rankDiff !== 0) return rankDiff;
        return (a.name || '').localeCompare(b.name || '');
      }
    }
  });
}

/**
 * Format category label for headers and titles
 * e.g., ['Supplement', 'Hormones & AAS'] -> "Supplements & Hormones & AAS"
 */
export function formatCategoryLabel(categories = [], fallbackCategory = 'All Compounds') {
  if (Array.isArray(categories) && categories.length > 0) {
    const normalized = categories.map((c) => normalizeCompoundCategory(c));
    const hasSupp = normalized.includes('Supplement');
    const hasPep = normalized.includes('Peptide');
    const hasHorm = normalized.includes('Hormones & AAS');

    if (hasSupp && hasPep && hasHorm) {
      return 'All Compounds';
    }

    const parts = [];
    if (hasSupp) parts.push('Supplements');
    if (hasPep) parts.push('Peptides');
    if (hasHorm) parts.push('Hormones & AAS');

    return parts.length > 0 ? parts.join(' & ') : 'Selected Categories';
  }

  if (fallbackCategory === 'Supplements') return 'Supplements';
  if (fallbackCategory === 'Peptides') return 'Peptides';
  const normalizedFallback = normalizeCompoundCategory(fallbackCategory);
  if (normalizedFallback === 'Hormones & AAS') return 'Hormones & AAS';

  return fallbackCategory || 'All Compounds';
}
